import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchEmployerSubscriptions,
  fetchEmployerSubscriptionsHistory,
  fetchEmployerSubscriptionsPaymentCheck,
} from "./EmployerSubscriptionsAPI";

// Keep state domains separate so they don't overwrite each other
const initialState = {
  current: null, // from fetchEmployerSubscriptionsAsync
  history: {
    current_page: 1,
    data: [],
    first_page_url: null,
    from: 0,
    last_page: 1,
    last_page_url: null,
    links: [],
    next_page_url: null,
    path: null,
    per_page: 15,
    prev_page_url: null,
    to: 0,
    total: 0,
  },
  paymentCheck: null,
  loading: {
    current: false,
    history: false,
    paymentCheck: false,
  },
  error: {
    current: null,
    history: null,
    paymentCheck: null,
  },
};

export const fetchEmployerSubscriptionsAsync = createAsyncThunk(
  "EmployerSubscriptions/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchEmployerSubscriptions();
      const resp = data.response;
      return resp;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// UPDATED: accept page param and return full response (with pagination)
export const fetchEmployerSubscriptionsHistoryAsync = createAsyncThunk(
  "EmployerSubscriptionsHistory/fetch",
  async (page = 1, { rejectWithValue }) => {
    try {
      const data = await fetchEmployerSubscriptionsHistory(page);
      const resp = data.response; // include pagination fields
      return resp;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchEmployerSubscriptionsPaymentCheckAsync = createAsyncThunk(
  "EmployerSubscriptionsPaymentCheck/fetch",
  async (uuid, { rejectWithValue }) => {
    try {
      const data = await fetchEmployerSubscriptionsPaymentCheck(uuid);
      const resp = data.response;
      return resp;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const EmployerSubscriptionsSlice = createSlice({
  name: "EmployerSubscriptions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // CURRENT (non-history)
      .addCase(fetchEmployerSubscriptionsAsync.pending, (state) => {
        state.loading.current = true;
        state.error.current = null;
      })
      .addCase(fetchEmployerSubscriptionsAsync.fulfilled, (state, action) => {
        state.loading.current = false;
        state.current = action.payload;
        state.error.current = null;
      })
      .addCase(fetchEmployerSubscriptionsAsync.rejected, (state, action) => {
        state.loading.current = false;
        state.error.current = action.payload || action.error.message;
      })

      // HISTORY (paginated)
      .addCase(fetchEmployerSubscriptionsHistoryAsync.pending, (state) => {
        state.loading.history = true;
        state.error.history = null;
      })
      .addCase(
        fetchEmployerSubscriptionsHistoryAsync.fulfilled,
        (state, action) => {
          state.loading.history = false;
          // Replace history object with the full paginated payload
          state.history = {
            current_page: action.payload?.current_page ?? 1,
            data: action.payload?.data ?? [],
            first_page_url: action.payload?.first_page_url ?? null,
            from: action.payload?.from ?? 0,
            last_page: action.payload?.last_page ?? 1,
            last_page_url: action.payload?.last_page_url ?? null,
            links: action.payload?.links ?? [],
            next_page_url: action.payload?.next_page_url ?? null,
            path: action.payload?.path ?? null,
            per_page: action.payload?.per_page ?? 15,
            prev_page_url: action.payload?.prev_page_url ?? null,
            to: action.payload?.to ?? 0,
            total: action.payload?.total ?? 0,
          };
          state.error.history = null;
        }
      )
      .addCase(
        fetchEmployerSubscriptionsHistoryAsync.rejected,
        (state, action) => {
          state.loading.history = false;
          state.error.history = action.payload || action.error.message;
        }
      )

      // PAYMENT CHECK
      .addCase(fetchEmployerSubscriptionsPaymentCheckAsync.pending, (state) => {
        state.loading.paymentCheck = true;
        state.error.paymentCheck = null;
      })
      .addCase(
        fetchEmployerSubscriptionsPaymentCheckAsync.fulfilled,
        (state, action) => {
          state.loading.paymentCheck = false;
          state.paymentCheck = action.payload;
          state.error.paymentCheck = null;
        }
      )
      .addCase(
        fetchEmployerSubscriptionsPaymentCheckAsync.rejected,
        (state, action) => {
          state.loading.paymentCheck = false;
          state.error.paymentCheck = action.payload || action.error.message;
        }
      );
  },
});

// Selectors
export const selectEmployerSubscriptions = (state) =>
  state.employerSubscriptions.current;

export const selectEmployerSubscriptionsHistory = (state) =>
  state.employerSubscriptions.history;

export const selectEmployerSubscriptionsHistoryLoading = (state) =>
  state.employerSubscriptions.loading.history;

export default EmployerSubscriptionsSlice.reducer;